import { getEditorFiles, leaveEdition, resolveFileError, sendEditorFile } from "@/services/editor.service";
import {
  getAudioFileInfo,
  getFileName,
  getFileType,
  getImageFileInfo,
  getReplacedFileType,
} from "@/services/file.service";
import {
  type Athlete,
  type EditorFileInfo,
  type File,
} from "@/types/file.types";
import type { User } from "@/types/user.types";
import { errorHandler } from "@/utils/errorHandler";
import { defineStore } from "pinia";
import { useGlobalStore } from "./globalStore";

export const useEditorStore = defineStore("editor", {
  state: () => ({
    envelopeId: "",
    newFileForFront: null as any,
    athlete: {} as Athlete,
    fileToReplace: {} as File,
    fileInfo: {} as EditorFileInfo,
    fileToEditInfo: {} as EditorFileInfo,
    fileToEdit: {} as File,
    fileErrors: [] as string[],
    validator: {} as User,
    fileComment: "",
    newFile: null as any,
    progress: 0,
    waitingFetch: false,
  }),
  actions: {
    /// CALL API ///

    // Get file to edit and initial file if needed
    async fetchFileToEdit(errorId: string) {
      const global = useGlobalStore();
      this.waitingFetch = true;
      global.showLoader = true;
      try {
        const res = await getEditorFiles(errorId);
        if (!res) {
          console.log("No files to process.");
          return;
        }
        this.envelopeId = res.id;
        this.athlete = res.athlete;
        this.fileToReplace = res.fileToReplace;
        this.fileErrors = res.errorCodes;
        this.fileComment = res.comment || "";
        if( res.fileToEdit ){
          this.fileToEdit = res.fileToEdit ;
        }
        if( this.fileToReplace ){
          this.fileInfo = await this.getFileInfo(this.fileToReplace);
        }
        if( this.fileToEdit ){
          this.fileToEditInfo = await this.getFileInfo(this.fileToEdit);
        }
        this.validator = res.validator || ({} as User);
        if(res.envelopeStatus){
          return res.envelopeStatus;
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showLoader = false;

        setTimeout(() => {
          this.waitingFetch = false;
        }, 500);
      }
    },


    async resolveFileError(errorId:string){
      try {
        await resolveFileError(errorId); 
        return true;
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
        return false;
        }
    },


    // Check if file has been edited by another validator and get athlete specific validation if exists
    async checkSpecificEdition(envelopeId: string) {
          try {
            const res = await getEditorFiles(envelopeId);
            if (!res) {
              console.log("No files to process.");
              return;
            }
            return {envelopeStatus: res.envelopeStatus};
          } catch (error) {
            if (error instanceof Error) {
              errorHandler(error);
            }
          }
        },

    // Send new file to replace file to edit
    async sendNewFile(errorId: string) {
      const global = useGlobalStore();
      global.showProgressLoader = true;
      try {
        await sendEditorFile(this.newFile, this.envelopeId);
        this.clearEditorStore();
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      } finally {
        global.showProgressLoader = false;
        this.progress = 0;
      }
    },

    async leaveEdition() {
          try {        
            if(!this.envelopeId){
              return;
            }
            await leaveEdition(this.envelopeId);
          } catch (error) {
            if (error instanceof Error) {
              errorHandler(error);
            }
          }
        },

    /// FILES INFO LOGIC ///

    // get size if image file or duration if audio file
    async getFileInfo(file: File) {
      if (!file.s3url) return this.getDefaultFileInfo();
      try {
        switch (file.type) {
          case "image": {
            const { width, height } = await getImageFileInfo(file.s3url);
            return this.formatFileInfo(
              `${width}x${height}px`,
              "00:00",
              file.s3url
            );
          }
          case "audio": {
            const duration = await getAudioFileInfo(file.s3url);
            return this.formatFileInfo("", duration, file.s3url);
          }
          default:
            return this.getDefaultFileInfo();
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
        return this.getDefaultFileInfo();
      }
    },

    // get size if image file or duration if audio file of the new file
    async getReplacedFileInfo(file: any, type: string) {
      if (!file) return this.getDefaultFileInfo();
      try {
        switch (type) {
          case "image": {
            const { width, height } = await getImageFileInfo(file);
            return this.formatReplacedFileInfo(
              `${width}x${height}px`,
              "00:00",
              file
            );
          }
          case "audio": {
            const duration = await getAudioFileInfo(file);
            return this.formatReplacedFileInfo("", duration, file);
          }
          default:
            return this.getDefaultFileInfo();
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
        return this.getDefaultFileInfo();
      }
    },

    getDefaultFileInfo() {
      return {
        dimensions: "",
        duration: "",
        name: "",
        type: "unknown",
      };
    },

    formatFileInfo(dimensions: string, duration: string, path: string) {
      return {
        dimensions,
        duration,
        name: getFileName(path),
        type: getFileType(path),
      };
    },

    formatReplacedFileInfo(dimensions: string, duration: string, path: string) {
      return {
        dimensions,
        duration,
        name: "",
        type: getReplacedFileType(path),
      };
    },

    /// EDITOR VIEW LOGIC ///

    clearEditorStore() {
      this.envelopeId = "";
      this.athlete = {} as Athlete;
      this.fileInfo = {} as EditorFileInfo;
      this.fileComment = "";
      this.fileToReplace = {} as File;
      this.newFile = null;
      this.newFileForFront = null;
      this.fileErrors = [] as string[];
      this.fileToEdit = {} as File;
      this.fileToEditInfo = {} as EditorFileInfo;
      this.validator = {} as User;
    },

    // load new file and display it instead of file to replace
    async selectNewFile(event: Event): Promise<void> {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (
        file &&
        (file.type.startsWith("image/") || file.type.startsWith("audio/"))
      ) {
        this.newFile = file;
        const reader = new FileReader();
        reader.onload = async (e: ProgressEvent<FileReader>) => {
          if (e.target) {
            this.newFileForFront = e.target.result as string;
            let type = file.type.startsWith("image/") ? "image" : "audio";
            this.fileInfo = await this.getReplacedFileInfo(
              this.newFileForFront,
              type
            );
          }
        };
        reader.readAsDataURL(file);
      }
    },
  },
});
